import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardContent, Stack } from "@mui/material";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { type ClipboardScorePolicy } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { ShieldIcon } from "@src/appV2/redesign/EmergencyCredits/components/ShieldIcon";
import { Card, Text } from "@src/appV2/redesign/system/ui-components";

import { ClipboardScoreActions } from "../../utils/constants";
import {
  calculateEmergencyCreditPointsSaved,
  getActionLabel,
  getConsequence,
} from "../../utils/utils";
import { ActivityItem } from "./Item";
import { getCaption, getLogDetails, type ItemDetails } from "./itemForLogUtils";

export interface ActivityItemForLogProps {
  id: string;
  log: ClipboardScoreLog;
  isDynamicPenaltiesEnabled: boolean;
  showDetails: boolean;
  expanded?: boolean;
  useExpectedArrivalTime?: boolean;
  onClick?: (id: string, isExpanded: boolean) => void;
  policy?: ClipboardScorePolicy;
  isEmergencyCreditsEnabled?: boolean;
}

function ItemDetailRow({ itemDetail }: { itemDetail: ItemDetails }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Text
        variant="body2"
        color={(theme) => (itemDetail.isNegative ? theme.palette.intent?.error.text : undefined)}
      >
        {itemDetail.label}
      </Text>
      {typeof itemDetail.value === "string" ? (
        <Text
          variant="body2"
          color={(theme) => (itemDetail.isNegative ? theme.palette.intent?.error.text : undefined)}
        >
          {itemDetail.value}
        </Text>
      ) : (
        itemDetail.value
      )}
    </Stack>
  );
}

export function ActivityItemForLog({
  id,
  log,
  isDynamicPenaltiesEnabled,
  showDetails,
  expanded,
  onClick,
  useExpectedArrivalTime,
  policy,
  isEmergencyCreditsEnabled = false,
}: ActivityItemForLogProps) {
  const label = getActionLabel(log);
  const consequence = getConsequence({
    action: log.action,
    score: log.clipboardScoreUpdatedScore ?? 0,
    adjustment: log.clipboardScoreAdjustment ?? 0,
  });
  const multiplier = isDynamicPenaltiesEnabled ? Number(log.clipboardScoreMultiplier) : undefined;
  const usedEmergencyCredit = isEmergencyCreditsEnabled && (log.usedEmergencyCredit ?? false);
  const isCancellation = log.action === ClipboardScoreActions.CANCEL_SHIFT;
  const isLateness = log.action === ClipboardScoreActions.START_SHIFT_LATE;

  const getEmergencyCreditPointsSaved = (): number | undefined => {
    if (!isEmergencyCreditsEnabled) {
      return undefined;
    }

    if (isCancellation) {
      return calculateEmergencyCreditPointsSaved(log.clipboardScoreAdjustment, policy?.cancelShift);
    }

    if (isLateness) {
      return calculateEmergencyCreditPointsSaved(
        log.clipboardScoreAdjustment,
        policy?.latenessConfig
      );
    }

    return undefined;
  };

  const emergencyCreditPointsSaved = getEmergencyCreditPointsSaved();
  const showEmergencyCreditIcon = usedEmergencyCredit && (isCancellation || isLateness);
  const caption = getCaption({
    action: log.action,
    consequence,
    multiplier,
    usedEmergencyCredit,
    ...(isDefined(log.reason) && { reason: log.reason }),
  });
  const details = getLogDetails({
    log,
    consequence,
    showShortLeadTimeInfo: useExpectedArrivalTime && isDefined(log.targetArrivalTime),
    usedEmergencyCredit,
    emergencyCreditPointsSaved,
  });

  const handleChange = () => {
    if (isDefined(onClick) && isDefined(expanded)) {
      onClick(id, expanded);
    }
  };

  return (
    <ActivityItem
      label={label}
      consequence={consequence}
      multiplier={multiplier}
      caption={caption}
      expanded={expanded}
      setExpanded={handleChange}
      customIcon={
        showEmergencyCreditIcon ? (
          <ShieldIcon variant="outlined" sx={{ fontSize: "0.75rem" }} />
        ) : undefined
      }
    >
      {showDetails && (
        <Stack paddingLeft={7} paddingTop={3}>
          <Card variant="quaternary">
            <CardContent>
              <Stack spacing={3}>
                {isDefined(details.callout) && <Box paddingBottom={1}>{details.callout}</Box>}

                {details.itemDetails.map((itemDetail) => (
                  <ItemDetailRow key={itemDetail.label} itemDetail={itemDetail} />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}
    </ActivityItem>
  );
}
